import moment, { Moment } from 'moment'
import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslations } from '../../featureSliced/shared/hooks/useTranslations'
import { RenderIf } from '../RenderIf'
import { TimeHeader, TimeHeaderContainer } from './styled'
import './styled.css'
import { WeekHandler } from './WeekHandler'

import { TimePicker } from './TimePicker'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { getHourRange } from 'featureSliced/shared/utils/getEarliestPickup'

const TIME_START = 6,
  TIME_END = 18

type CalendarProps = {
  hideCalendar?: boolean
  showTime?: boolean
  minDateGap?: number
  minDate?: Moment
  defaultValue?: Moment
  onUpdate?: (value: Moment) => void
}

export const Calendar: FC<CalendarProps> = ({
  hideCalendar = false,
  showTime = false,
  minDateGap = 0,
  minDate,
  defaultValue,
  onUpdate,
}) => {
  const t = useTranslations()
  const { restaurants } = useMySelector((state) => state)

  const [value, setValue] = useState<Moment>(
    defaultValue ?? moment().add(minDateGap, 'days').set({ h: TIME_START })
  )

  useEffect(() => {
    if (onUpdate) {
      onUpdate(value)
    }
  }, [value])

  const setNewDate = (newDate: Moment) => {
    setValue((prevValue) =>
      moment(prevValue).set({ D: newDate.date(), M: newDate.month() })
    )
  }

  const setTime = (name: string, value: string) => {
    if (name === 'hours') {
      setHours(value)
    }
    if (name === 'minutes') {
      setMinutes(value)
    }
  }

  const setHours = (newHours: string) => {
    const numericHours = parseInt(newHours, 10)
    if (Number.isNaN(numericHours)) return
    setValue((prevValue) => moment(prevValue).set({ h: numericHours }))
    if (numericHours >= TIME_END) {
      setValue((prevValue) => moment(prevValue).set({ m: 0 }))
    }
  }

  const setMinutes = (newMinutes: string) => {
    const numericMinutes = parseInt(newMinutes, 10)
    if (Number.isNaN(numericMinutes)) return
    setValue((prevValue) => {
      if (+moment(prevValue).format('HH') < TIME_END) {
        return moment(prevValue).set({ m: numericMinutes })
      }
      return prevValue
    })
  }

  const getMinDate = () => {
    return minDate ?? moment().add(minDateGap, 'days')
  }

  const hourRange = useMemo(
    () =>
      getHourRange(
        moment(),
        value,
        (restaurants ?? []).map((restaurant) => restaurant.schedule),
        value.hours()
      ),
    [value, restaurants]
  )

  return (
    <div data-testid="calendar_test-id">
      <RenderIf condition={!hideCalendar}>
        <WeekHandler
          value={value}
          onChange={setNewDate}
          minDate={getMinDate()}
        />
      </RenderIf>
      <RenderIf condition={showTime}>
        <TimeHeader>{t('clickCollect.chooseTime')}</TimeHeader>
        <TimeHeaderContainer>
          <TimePicker
            hours={value.format('HH')}
            minutes={value.format('mm')}
            hoursStart={hourRange.hoursStart}
            hoursEnd={hourRange.hoursEnd}
            minutesStart={hourRange.minutesStart}
            handleTime={setTime}
          />
        </TimeHeaderContainer>
      </RenderIf>
    </div>
  )
}
